import { useEffect, useState } from "react";
import { Mail, Paperclip, Pin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { preconApi } from "../lib/api";
import PersonChip from "./PersonChip";
import MentionTextarea from "./MentionTextarea";
import { openBid } from "../lib/navigation";
import { StatusChip, StageChip } from "./BidStatusChips";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" }) : "");
const d10 = (iso) => (iso ? iso.slice(0, 10) : "—");
const KIND = { itb: "ITB", inquiry: "Inquiry", addendum: "Addendum", date_change: "Date change", proposal: "Proposal", budget: "Budget", request_approval: "Request Approval", decline: "Decline", handoff: "Handoff" };

/** BID-2 §5 — bid card header: name, customers, status/stage, both due dates, people, location, dates, links. */
export function BidCardHeader({ job, canEdit, onChanged }) {
  const go = (tab) => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab } }));
  return (
    <div className="space-y-1.5 text-[11px] text-muted-foreground" data-testid="bid-card-header">
      <div className="flex flex-wrap items-center gap-1.5">
        {(job.invitations || []).map((i) => <span key={i.id} className="rounded border border-border px-1.5 py-0.5">{i.gc_name}</span>)}
        <StatusChip job={job} canEdit={canEdit} onChanged={onChanged} />
        <StageChip job={job} />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span data-testid="bid-card-due">Bid due <b className="text-foreground">{d10(job.bid_due_internal)}</b> internal · GC {d10(job.bid_due_at)}</span>
        <span className="inline-flex items-center gap-1">Est. <PersonChip name={job.estimator_name || job.estimator_email} /> {job.estimator_name || "—"}</span>
        <span className="inline-flex items-center gap-1">AM <PersonChip name={job.account_manager_name || job.account_manager_email} /> {job.account_manager_name || "—"}</span>
        <span>{[job.street, job.city, job.state].filter(Boolean).join(", ") || "no location"}</span>
        <span>Start {d10(job.start_date)} · Completion {d10(job.completion_date)}</span>
        {job.estimate_id && <button type="button" className="underline" onClick={() => openBid(job.id, "estimate")} data-testid="bid-card-link-proposal">Estimate workspace</button>}
        {job.pipeline_project_id && <button type="button" className="underline" onClick={() => go("pipeline")} data-testid="bid-card-link-pipeline">Pipeline</button>}
        {job.bod_count > 0 && <button type="button" className="underline" onClick={() => go("bod")} data-testid="bid-card-link-bod">BOD · {job.bod_count}</button>}
      </div>
    </div>
  );
}

/** BID-2 §3 — every email linked to this bid, grouped by thread. References only. */
export function EmailsTab({ jobId }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState("");
  useEffect(() => { preconApi.jobEmails(jobId).then(setData).catch(() => setData({ items: [], threads: [] })); }, [jobId]);
  if (!data) return <div className="p-4 text-xs text-muted-foreground">loading…</div>;
  if (!data.items.length) return <div className="p-4 text-xs text-muted-foreground" data-testid="bid-emails-empty">No emails are linked to this bid yet.</div>;
  return (
    <div className="divide-y divide-border" data-testid="bid-emails">
      {data.threads.map((t) => (
        <div key={t.key} className="py-2" data-testid={`email-thread-${t.ids[0]}`}>
          <div className="px-1 text-[10px] uppercase tracking-wide text-muted-foreground">{t.subject || "(no subject)"} · {t.ids.length}</div>
          {t.ids.map((id) => { const m = data.items.find((x) => x.id === id); return (
            <button key={id} type="button" onClick={() => setOpenId(openId === id ? "" : id)} data-testid={`email-row-${id}`}
                    className="flex w-full items-center gap-2 rounded px-1 py-1 text-left text-xs hover:bg-muted/50">
              <Mail className={`h-3.5 w-3.5 ${m.direction === "inbound" ? "text-sky-600" : "text-emerald-600"}`} />
              <span className="w-24 shrink-0 rounded bg-muted px-1 text-[10px]">{KIND[m.kind] || m.kind}{m.linked_by_hand ? " · linked" : ""}</span>
              <span className="w-40 shrink-0 truncate text-muted-foreground">{m.direction === "inbound" ? m.from : (m.to || []).join(", ")}</span>
              <span className="flex-1 truncate text-foreground">{m.subject}</span>
              {m.attachments?.length > 0 && <Paperclip className="h-3 w-3 text-muted-foreground" title={m.attachments.join(", ")} />}
              <span className="shrink-0 text-muted-foreground">{fmt(m.at)}</span>
            </button>); })}
          {t.ids.includes(openId) && <pre className="mx-1 mt-1 whitespace-pre-wrap rounded border border-border bg-muted/30 p-2 text-[11px] font-sans" data-testid={`email-open-${openId}`}>{data.items.find((x) => x.id === openId)?.snippet || "(no preview)"}</pre>}
        </div>))}
    </div>
  );
}

/** BID-2 §4 — notes: composer on top, thread newest first, @mention autocomplete, nothing edited or deleted. */
export function NotesPanel({ job, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    try { await preconApi.addNote(job.id, text.trim()); setText(""); onChanged?.(); toast.success("Note added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Note failed"); } finally { setBusy(false); }
  };
  const thread = [...(job.notes || [])].reverse();
  return (
    <div className="space-y-3" data-testid="bid-notes-panel">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <MentionTextarea value={text} onChange={setText} onSubmit={submit} rows={1}
                           className="min-h-[36px] resize-none py-2 text-xs"
                           placeholder="Add a note… @name to mention · Enter to post · Shift+Enter for a new line" />
        </div>
        <Button size="sm" className="h-9 text-xs" onClick={submit} disabled={busy || !text.trim()} data-testid="note-submit"><Send className="mr-1 h-3 w-3" /> Post</Button>
      </div>
      <ul className="space-y-2" data-testid="bid-notes-thread">
        {thread.map((n) => (
          <li key={n.id} className="rounded border border-border p-2 text-xs" data-testid={`note-${n.id}`}>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground"><PersonChip name={n.author_name} /><span className="font-medium text-foreground">{n.author_name}</span><span>{fmt(n.created_at)}</span>{n.pinned && <Pin className="h-3 w-3" />}
              {n.mentions?.length > 0 && <span data-testid={`note-mentions-${n.id}`}>→ {n.mentions.join(", ")}</span>}</div>
            <div className="mt-1 whitespace-pre-wrap text-foreground">{n.text}</div>
          </li>))}
        {!thread.length && <li className="text-[11px] text-muted-foreground">No notes yet.</li>}
      </ul>
    </div>
  );
}
